import { useState, useRef } from 'react';
import CONDITIONS, { PROJECT_CONDITIONS } from '../../config/conditions';
import PhotoPickerModal from '../PhotoPickerModal';
import { compressImageFile } from '../../utils/imageUtils';

const TABS = ['Paste URL', 'Upload Photos'];

const uploadZoneStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  padding: '32px 24px',
  border: '2px dashed var(--border)',
  borderRadius: 'var(--radius-md)',
  cursor: 'pointer',
  transition: 'border-color 0.2s',
};

export default function StepListingInput({ data, update, onNext, onBack }) {
  const [activeTab, setActiveTab] = useState(0);
  const [showPhotoPicker, setShowPhotoPicker] = useState(false);
  const fileRef = useRef(null);

  const canProceed = data.listingUrl.trim() || data.listingPhotos.length > 0;

  function handleFiles(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const remaining = 10 - data.listingPhotos.length;
    const toProcess = files.slice(0, remaining);

    toProcess.forEach((file) => {
      compressImageFile(file, 1200, 0.8).then((dataUrl) => {
        update({
          listingPhotos: [
            ...data.listingPhotos,
            {
              id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
              dataUrl,
              label: file.name,
            },
          ],
        });
      }).catch(() => {});
    });

    if (fileRef.current) fileRef.current.value = '';
  }

  function removePhoto(id) {
    update({ listingPhotos: data.listingPhotos.filter((p) => p.id !== id) });
  }

  return (
    <>
      <div className="screen-header">
        <h1 className="screen-title">Share the listing details</h1>
        <p className="screen-subtitle">
          Paste a URL from any marketplace or upload photos of the listing.
        </p>
      </div>

      <div style={{ flex: 1, paddingTop: 24 }}>
        {/* Tab bar */}
        <div
          style={{
            display: 'flex',
            gap: 0,
            marginBottom: 20,
            borderBottom: '1.5px solid var(--border)',
          }}
        >
          {TABS.map((tab, i) => (
            <button
              key={tab}
              onClick={() => setActiveTab(i)}
              style={{
                flex: 1,
                padding: '10px 0',
                fontSize: '0.875rem',
                fontWeight: 600,
                color: activeTab === i ? 'var(--accent)' : 'var(--text-muted)',
                borderBottom:
                  activeTab === i
                    ? '2px solid var(--accent)'
                    : '2px solid transparent',
                transition: 'all 0.2s',
                textAlign: 'center',
              }}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* URL Tab */}
        {activeTab === 0 && (
          <div className="form-group">
            <label className="form-label">Listing URL</label>
            <input
              className="form-input"
              type="url"
              placeholder="https://bringatrailer.com/listing/..."
              value={data.listingUrl}
              onChange={(e) => update({ listingUrl: e.target.value })}
            />
            <p
              style={{
                fontSize: '0.75rem',
                color: 'var(--text-muted)',
                marginTop: 6,
              }}
            >
              Supports BaT, Cars & Bids, eBay Motors, AutoTrader, and more
            </p>
          </div>
        )}

        {/* Photos Tab */}
        {activeTab === 1 && (
          <div>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFiles}
              style={{ display: 'none' }}
            />
            {data.listingPhotos.length < 10 && (
              <button type="button" onClick={() => setShowPhotoPicker(true)} style={uploadZoneStyle}>
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="var(--text-muted)"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
                <span
                  style={{
                    color: 'var(--text-secondary)',
                    fontSize: '0.875rem',
                  }}
                >
                  Tap to upload photos ({data.listingPhotos.length}/10)
                </span>
              </button>
            )}

            {data.listingPhotos.length > 0 && (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: 8,
                  marginTop: 12,
                }}
              >
                {data.listingPhotos.map((photo) => (
                  <div
                    key={photo.id}
                    style={{ position: 'relative', aspectRatio: '1' }}
                  >
                    <img
                      src={photo.dataUrl}
                      alt={photo.label}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        borderRadius: 'var(--radius-sm)',
                        border: '1px solid var(--border)',
                      }}
                    />
                    <button
                      onClick={() => removePhoto(photo.id)}
                      style={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        background: 'rgba(0,0,0,0.7)',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.875rem',
                        lineHeight: 1,
                      }}
                    >
                      &times;
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Shared optional fields */}
        <div className="form-group" style={{ marginTop: 20 }}>
          <label className="form-label">Asking Price (Optional)</label>
          <input
            className="form-input"
            type="text"
            inputMode="numeric"
            placeholder="e.g. $35,000"
            value={data.askingPrice}
            onChange={(e) => update({ askingPrice: e.target.value })}
          />
        </div>

        {/* Vehicle Condition — optional override for listings */}
        <div className="form-group">
          <label className="form-label">
            Vehicle Condition{' '}
            <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: '0.75rem' }}>
              (If different from listing description)
            </span>
          </label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {CONDITIONS.map((cond) => (
              <button
                key={cond.value}
                onClick={() =>
                  update({ condition: data.condition === cond.value ? '' : cond.value })
                }
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 12,
                  padding: '12px 14px',
                  borderRadius: 'var(--radius-sm)',
                  border: data.condition === cond.value
                    ? '1.5px solid var(--accent)'
                    : '1.5px solid var(--border)',
                  background: data.condition === cond.value
                    ? 'var(--accent-subtle)'
                    : 'var(--bg-elevated)',
                  textAlign: 'left',
                  transition: 'all 0.15s ease',
                  cursor: 'pointer',
                }}
              >
                <div
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    border: data.condition === cond.value
                      ? '5px solid var(--accent)'
                      : '2px solid var(--text-muted)',
                    flexShrink: 0,
                    marginTop: 1,
                    transition: 'border 0.15s ease',
                  }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-primary)' }}>
                    {cond.value}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: 2, lineHeight: 1.3 }}>
                    {cond.description}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Project car warning */}
        {PROJECT_CONDITIONS.includes(data.condition) && (
          <div
            style={{
              background: 'var(--bg-elevated)',
              border: '1px solid var(--warning)',
              borderRadius: 'var(--radius-sm)',
              padding: '10px 14px',
              marginBottom: 16,
              fontSize: '0.8125rem',
              color: 'var(--warning)',
              lineHeight: 1.4,
            }}
          >
            {'\u26A0\uFE0F'} This vehicle requires significant work. Our analysis focuses on project
            viability, not typical ownership costs.
          </div>
        )}

        <div style={{ display: 'flex', gap: 12 }}>
          <div className="form-group" style={{ flex: 1 }}>
            <label className="form-label">Budget Min</label>
            <input
              className="form-input"
              type="text"
              inputMode="numeric"
              placeholder="$25,000"
              value={data.budgetMin}
              onChange={(e) => update({ budgetMin: e.target.value })}
            />
          </div>
          <div className="form-group" style={{ flex: 1 }}>
            <label className="form-label">Budget Max</label>
            <input
              className="form-input"
              type="text"
              inputMode="numeric"
              placeholder="$40,000"
              value={data.budgetMax}
              onChange={(e) => update({ budgetMax: e.target.value })}
            />
          </div>
        </div>
      </div>

      <div className="nav-row">
        <button className="btn btn-secondary" onClick={onBack}>
          Back
        </button>
        <button className="btn btn-primary" disabled={!canProceed} onClick={onNext}>
          Analyze
        </button>
      </div>

      <PhotoPickerModal
        show={showPhotoPicker}
        onClose={() => setShowPhotoPicker(false)}
        onSelect={(source) => {
          setShowPhotoPicker(false);
          fileRef.current.removeAttribute('capture');
          fileRef.current.click();
        }}
      />
    </>
  );
}
